import React, { useRef, useState, useEffect } from 'react';
import { Box, Grid2 } from '@mui/material';
import { Form, Formik, type FormikProps } from 'formik';
import * as Yup from 'yup';
import {
  CustomDialog,
  CustomDialogContent,
  CustomDivider,
  CustomSelect,
  CustomTextField,
  CustomTypography,
} from '@qler-view/shared_lib/src';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import {
  useGetContactDetailsById,
  useGetContactUsMetaData,
  useSaveOrUpdateContact,
} from '../../API/hooks/ContactUsHook';
import { useNavigate } from 'react-router-dom';
import { type ContactUsType } from '../../API/services/ContactUsService';
import formatPhoneNumber from '../components/FormatPhoneNumber';
import { FACILITY_VIEW_ROUTE_PATH } from '../../routers/routePaths';

export interface AddNewContactFaciltyProps {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  contactId?: string;
  mode: string;
}

export const contactUsInitialValues: ContactUsType = {
  firstName: '',
  lastName: '',
  suffix: '',
  title: '',
  department: '',
  primaryEmail: '',
  otherEmail: '',
  primaryPhone: '',
  mobilePhone: '',
  otherPhone: '',
  categoryId: 0,
  notes: '',
  category: '',
};

const contactUsValidationSchema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  suffix: Yup.string().nullable(),
  title: Yup.string().required('Title is required'),
  department: Yup.string().required('Department is required'),
  primaryEmail: Yup.string()
    .email('Invalid email address')
    .required('Primary Email is required'),
  otherEmail: Yup.string().email('Invalid email address').nullable(),
  primaryPhone: Yup.string().required('Primary Phone is required'),
  mobilePhone: Yup.string().nullable(),
  otherPhone: Yup.string().nullable(),
  categoryId: Yup.string().nullable(),
});

const AddNewContactFacility: React.FC<AddNewContactFaciltyProps> = ({
  open,
  onClose,
  facilityId,
  contactId,
  mode,
}) => {
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<ContactUsType>>(null);
  const userId = localStorage.getItem('userId') ?? '0';

  const { CategoryMetaData } = useGetContactUsMetaData(userId);
  const { ContactDetailsById, isFetching } = useGetContactDetailsById(
    userId,
    Number(contactId)
  );
  const [initialValues, setInitialValues] = useState(contactUsInitialValues);
  const [contactSubmitAction, setContactSubmitAction] = useState<string>('');

  useEffect(() => {
    if (mode === 'edit') {
      formikRef.current?.resetForm({
        values: contactUsInitialValues,
      });
    }
  }, [contactId]);

  useEffect(() => {
    if (mode === 'edit' && ContactDetailsById && !isFetching) {
      setInitialValues({
        id: ContactDetailsById?.id ?? 0,
        firstName: ContactDetailsById?.firstName ?? '',
        lastName: ContactDetailsById?.lastName ?? '',
        suffix: ContactDetailsById?.suffix ?? '',
        title: ContactDetailsById?.title ?? '',
        department: ContactDetailsById?.department ?? '',
        primaryEmail: ContactDetailsById?.primaryEmail ?? '',
        otherEmail: ContactDetailsById?.otherEmail ?? '',
        primaryPhone: ContactDetailsById?.primaryPhone ?? '',
        mobilePhone: ContactDetailsById?.mobilePhone ?? '',
        otherPhone: ContactDetailsById?.otherPhone ?? '',
        categoryId: ContactDetailsById?.categoryId ?? '',
        notes: ContactDetailsById?.notes ?? '',
      });
    }
  }, [ContactDetailsById, isFetching, mode]);

  const onSuccess = () => {
    if (contactSubmitAction) {
      navigate(FACILITY_VIEW_ROUTE_PATH, {
        state: { tab: 1, message: 'Contact Updated successfully!' },
      });
    } else {
      navigate(FACILITY_VIEW_ROUTE_PATH, {
        state: { tab: 1, message: 'Contact saved successfully!' },
      });
    }
    onClose();
  };

  const { mutate: saveOrUpdateContact } = useSaveOrUpdateContact(onSuccess);

  const handleContactSaveOrUpdate = async (
    contactData: typeof contactUsInitialValues
  ) => {
    const { id, ...rest } = contactData;

    const payload =
      contactSubmitAction === 'save'
        ? rest // exclude id
        : { id, ...rest }; // include id for update

    if (contactSubmitAction) {
      saveOrUpdateContact({
        userId,
        action: contactSubmitAction,
        facilityId,
        ...payload,
      });
    }
  };

  return (
    <CustomDialog
      id="cancel-consult-dialog"
      open={open}
      onClose={() => {
        formikRef.current?.resetForm({
          values: contactUsInitialValues,
        });
        onClose();
      }}
      disableEscapeKeyDown={true}
      maxWidth="lg"
      fullWidth
    >
      <CustomDialogContent className="add-new-contact-facility">
        <Box className="pb-32">
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <Box className="text-align-left">
                <CustomTypography
                  className="pb-4"
                  variant="h4"
                  id="add-new-contact"
                >
                  Add New Contact
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color="textDisabled"
                  id="add-new-contact-subtext"
                >
                  Add a new contact to your directory
                </CustomTypography>
              </Box>
            </Grid2>
          </Grid2>
        </Box>
        <Box>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validateOnMount={false}
            validationSchema={contactUsValidationSchema}
            onSubmit={(values) => {
              handleContactSaveOrUpdate(values);
            }}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, handleSubmit }) => (
              <Form>
                <Box>
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-20"
                        id="add-new-contact-facility-personal-info"
                      >
                        Personal Information
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Box className="display-flex pb-32">
                    <Grid2 container spacing={4} className="width-80">
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="personal-info-facility-firstname-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          First Name <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-first-name"
                          name="facilityName"
                          placeholder="Enter First Name"
                          size="small"
                          value={values.firstName}
                          onChange={(event) => {
                            setFieldValue('firstName', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.firstName && touched?.firstName && (
                          <CustomTypography
                            id="validate-contact-first-name"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.firstName}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="personal-info-facility-lastname-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Last Name <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-last-name"
                          name="facilityName"
                          placeholder="Enter Last Name"
                          size="small"
                          value={values.lastName}
                          onChange={(event) => {
                            setFieldValue('lastName', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.lastName && touched?.lastName && (
                          <CustomTypography
                            id="validate-contact-last-name"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.lastName}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="personal-info-facility-suffix-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Suffix
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-suffix"
                          name="facilityName"
                          placeholder="e.g., Jr., Sr., III"
                          size="small"
                          value={values.suffix}
                          onChange={(event) => {
                            setFieldValue('suffix', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.suffix && touched?.suffix && (
                          <CustomTypography
                            id="validate-contact-sufix"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.suffix}
                          </CustomTypography>
                        )}
                      </Grid2>
                    </Grid2>
                  </Box>
                  <CustomDivider />
                  <Grid2 container className="pt-32">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-20"
                        id="add-new-contact-facility-professional-info"
                      >
                        Professional Information
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Box className="display-flex pb-32">
                    <Grid2 container spacing={4} className="width-80">
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="professional-info-facility-title-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Title <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-title"
                          name="facilityName"
                          placeholder="e.g., Manager, Director"
                          size="small"
                          value={values.title}
                          onChange={(event) => {
                            setFieldValue('title', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.title && touched?.title && (
                          <CustomTypography
                            id="validate-contact-title"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.title}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="professional-info-facility-department-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Department <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-department"
                          name="facilityName"
                          placeholder="e.g., Sales, Engineering"
                          size="small"
                          value={values.department}
                          onChange={(event) => {
                            setFieldValue('department', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.department && touched?.department && (
                          <CustomTypography
                            id="validate-contact-department"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.department}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="professional-info-facility-category-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Category
                        </CustomTypography>
                        <CustomSelect
                          id="contact-category-select"
                          placeholder="Select Category"
                          size="small"
                          options={CategoryMetaData?.categoryList ?? []}
                          onChange={(value) => {
                            setFieldValue('categoryId', Number(value));
                          }}
                          selectedValue={values.categoryId}
                          fullWidth
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                  <CustomDivider />
                  <Grid2 container className="pt-32">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-20"
                        id="add-new-contact-facility-contact-info"
                      >
                        Contact Information
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Box className="display-flex pb-32">
                    <Grid2 container spacing={4} className="width-80">
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="contact-info-facility-primary-phone-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Primary Phone <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-primary-phone"
                          name="Primary Phone"
                          placeholder="Type Primary Phone"
                          size="small"
                          value={values.primaryPhone}
                          onChange={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('primaryPhone', formatted);
                          }}
                          onBlur={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('primaryPhone', formatted);
                          }}
                        ></CustomTextField>
                        {errors?.primaryPhone && touched?.primaryPhone && (
                          <CustomTypography
                            id="validate-contact-primary-phone"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.primaryPhone}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="contact-info-facility-mobile-phone-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Mobile Phone
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-mobile-phone"
                          name="mobilePhone"
                          placeholder="Type Mobile Phone"
                          size="small"
                          value={values.mobilePhone}
                          onChange={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('mobilePhone', formatted);
                          }}
                          onBlur={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('mobilePhone', formatted);
                          }}
                        ></CustomTextField>
                        {errors?.mobilePhone && touched?.mobilePhone && (
                          <CustomTypography
                            id="validate-contact-mobile-phone"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.mobilePhone}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="contact-info-facility-other-phone-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Other Phone
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-other-phone"
                          name="otherName"
                          placeholder="Type Other Phone"
                          size="small"
                          value={values.otherPhone}
                          onChange={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('otherPhone', formatted);
                          }}
                          onBlur={(event) => {
                            const formatted = formatPhoneNumber(
                              event.target.value
                            );
                            setFieldValue('otherPhone', formatted);
                          }}
                        ></CustomTextField>
                        {errors?.otherPhone && touched?.otherPhone && (
                          <CustomTypography
                            id="validate-contact-other-phone"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.otherPhone}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="contact-info-facility-primary-email-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Primary Email <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-primary-email"
                          name="facilityName"
                          placeholder="Type Primary Email"
                          size="small"
                          value={values.primaryEmail}
                          onChange={(event) => {
                            setFieldValue('primaryEmail', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.primaryEmail && touched?.primaryEmail && (
                          <CustomTypography
                            id="validate-contact-primary-email"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.primaryEmail}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="contact-info-facility-other-email-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Other Email
                        </CustomTypography>
                        <CustomTextField
                          id="txt-contact-other-email"
                          name="otherEmail"
                          placeholder="Type Other Email"
                          size="small"
                          value={values.otherEmail}
                          onChange={(event) => {
                            setFieldValue('otherEmail', event.target.value);
                          }}
                        ></CustomTextField>
                        {errors?.otherEmail && touched?.otherEmail && (
                          <CustomTypography
                            id="validate-contact-other-email"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.otherEmail}
                          </CustomTypography>
                        )}
                      </Grid2>
                    </Grid2>
                  </Box>
                  <CustomDivider />
                  <Grid2 container className="pt-32">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-20"
                        id="contact-info-facility-notes-label"
                      >
                        Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                      <CustomTypography
                        id="contact-notes-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Notes
                      </CustomTypography>
                      <CustomTextField
                        id="txt-contact-notes"
                        placeholder="Type Notes"
                        size="small"
                        multiLine
                        rows={4}
                        value={values.notes}
                        onChange={(event) => {
                          setFieldValue('notes', event.target.value);
                        }}
                      ></CustomTextField>
                      {errors?.notes && touched?.notes && (
                        <CustomTypography
                          id="validate-contact-notes"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.notes}
                        </CustomTypography>
                      )}
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Box className="display-flex justify-end">
                        <Box className="pr-12">
                          <CustomButton
                            id="contact-cancel-button"
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              formikRef.current?.resetForm();
                              onClose();
                            }}
                          >
                            Cancel
                          </CustomButton>
                        </Box>
                        <Box>
                          <CustomButton
                            id="contact-save-button"
                            color="primary"
                            variant="contained"
                            onClick={() => {
                              setContactSubmitAction(
                                contactId ? 'Update' : 'Save'
                              );
                              handleSubmit();
                            }}
                          >
                            {mode === 'edit' ? 'Update' : 'Save'}
                          </CustomButton>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </CustomDialogContent>
    </CustomDialog>
  );
};

export default AddNewContactFacility;
